import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore, DEMO_MODE } from '../../store/StoreContext.jsx';
import { describeProfileSave } from '../../store/profileSave.js';

// Getting at the data underneath without the page looking like a form.
//
// Every figure in the meeting is set as finished type, the way it prints. An
// advisor who needs to correct one clicks it, and a small card opens beside it
// with the questionnaire rows that make that figure, in plain inputs. Close
// the card and the page is a finished deliverable again, with the new number
// in it and nothing to show it was ever touched.
//
// Two levels of visibility, so the client sees a document and the advisor can
// still find the handles. At rest, an adjustable figure is indistinguishable
// from any other text until the pointer is over it, when a faint dotted rule
// appears under it. And with Adjust switched on (the toolbar button, or E),
// every adjustable figure on the page gets a soft wash, which is how an
// advisor finds them all at once before the client arrives.
//
// None of it exists on the takeaway or in print: outside the meeting a Peek
// renders its figure and nothing else.

const NOOP = () => {};
const MeetingContext = createContext({
  adjustable: false, drawing: false, adjusting: false, hidden: new Set(), hide: NOOP, show: NOOP, setMeeting: NOOP, people: [], assessmentSave: null, goalLead: null,
});

/**
 * What the meeting knows about itself: whether figures may be opened, whether
 * the marker is in hand, whether Adjust is on, which callouts the advisor has
 * left out, and how to save a choice against the assessment record.
 */
export function MeetingProvider({ adjustable, drawing, adjusting = false, offerEmpty = false, hidden, onHide = NOOP, onShow = NOOP, setMeeting = NOOP, people = [], assessmentSave = null, goalLead = null, children }) {
  const value = {
    goalLead,
    adjustable,
    drawing,
    adjusting,
    // Offer to fill an empty part (the goals) without the page's edit mode,
    // as the brief screen does (Jimmy, 2026-10-20).
    offerEmpty,
    people,
    assessmentSave,
    hidden: hidden instanceof Set ? hidden : new Set(hidden || []),
    hide: onHide,
    show: onShow,
    setMeeting,
  };
  return (
    <MeetingContext.Provider value={value}>
      {children}
    </MeetingContext.Provider>
  );
}

export function useMeetingContext() {
  return useContext(MeetingContext);
}

const GAP = 8;
const CARD_W = 420;

function place(anchor) {
  const r = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(CARD_W, vw - 24);
  const left = Math.max(12, Math.min(r.left, vw - width - 12));
  // Below the figure when there is room, above it when there is not.
  const below = vh - r.bottom;
  return below >= 280 || below >= r.top
    ? { left, top: r.bottom + GAP, width, maxHeight: Math.max(200, below - GAP - 16) }
    : { left, bottom: vh - r.top + GAP, width, maxHeight: Math.max(200, r.top - GAP - 16) };
}

/** The questionnaire's save state, in the meeting's words. */
export function useProfileSave() {
  const store = useStore();
  return describeProfileSave({ ...store, demo: DEMO_MODE });
}

// What the card says about saving. A figure from the questionnaire says
// whether the questionnaire's autosave has taken the change; one kept with the
// assessment says what the assessment sync says, and nothing when it has
// nothing to report, rather than claiming a save it has not seen.
function SaveLine({ saves }) {
  const profile = useProfileSave();
  const { assessmentSave } = useContext(MeetingContext);
  const state = saves === 'assessment' ? (DEMO_MODE ? profile : assessmentSave) : profile;
  if (!state) return null;
  return (
    <div className={`conv-save-line is-${state.tone}`} role="status" aria-live="polite">
      {state.text}
      {state.detail ? (
        <details className="conv-save-detail">
          <summary>What Salesforce said</summary>
          <code>{state.detail}</code>
        </details>
      ) : null}
    </div>
  );
}

function PeekCard({ anchor, title, note, saves, onClose, children }) {
  const card = useRef(null);
  const [pos, setPos] = useState(() => place(anchor));

  useLayoutEffect(() => {
    let frame = 0;
    const follow = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setPos(place(anchor)));
    };
    const outside = e => {
      if (card.current?.contains(e.target) || anchor.contains(e.target)) return;
      onClose();
    };
    const key = e => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); anchor.focus?.(); }
    };
    window.addEventListener('resize', follow);
    document.addEventListener('scroll', follow, true);
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', key, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', follow);
      document.removeEventListener('scroll', follow, true);
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', key, true);
    };
  }, [anchor, onClose]);

  // Focus the first input, so a correction is one click and some typing.
  useEffect(() => {
    const first = card.current?.querySelector('input, select, textarea');
    first?.focus?.({ preventScroll: true });
  }, []);

  // Inside the screen, so it inherits the assessment's own tokens and type,
  // but outside the meeting canvas, so it is never captured into a drawing.
  const host = anchor.closest('.conv-screen') || document.body;
  return createPortal(
    <div className="conv-peek-card" ref={card} role="dialog" aria-label={title} style={pos}>
      <div className="conv-peek-head">
        <span className="conv-eyebrow">{title}</span>
        <button type="button" className="conv-peek-close" aria-label="Close" onClick={onClose}>&times;</button>
      </div>
      <div className="conv-peek-body">{children}</div>
      <div className="conv-peek-foot">
        {note ? <div>{note}</div> : null}
        <SaveLine saves={saves} />
      </div>
    </div>,
    host,
  );
}

/**
 * A figure that opens onto the data it is made of.
 *
 * `editor` is a function so the card's contents are only built when it opens.
 * `saves` says where its edits go: the questionnaire (the default) or the
 * assessment record, so the card reports the right save.
 * `as` lets a Peek sit inside a table cell or a heading without changing it.
 */
export default function Peek({ title, note, saves = 'questionnaire', editor, children, className = '', as: Tag = 'span' }) {
  const { adjustable, drawing } = useContext(MeetingContext);
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => { if (drawing) setOpen(false); }, [drawing]);

  if (!adjustable || !editor) return <Tag className={className}>{children}</Tag>;

  const toggle = () => { if (!drawing) setOpen(v => !v); };
  return (
    <>
      <Tag
        ref={anchor}
        className={`conv-peek ${className}`}
        data-open={open ? 'true' : undefined}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${title}: adjust`}
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
        }}
      >
        {children}
      </Tag>
      {open && anchor.current && (
        <PeekCard anchor={anchor.current} title={title} note={note} saves={saves} onClose={close}>
          {editor()}
        </PeekCard>
      )}
    </>
  );
}
